import type { Kart } from '../game/kart';
import type { Vec2 } from '../core/types';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
}

interface Skid {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  life: number;
  max: number;
}

/** Drift-nyomok, szikrák, por, nitró-láng – mind kozmetika, semmi szimuláció. */
export class Fx {
  private particles: Particle[] = [];
  private skids: Skid[] = [];
  private lastRear = new Map<number, Vec2>();

  update(dt: number, karts: Kart[]): void {
    for (const k of karts) {
      if (k.finished) continue;
      const L = k.character.vehicle.length;
      const rearX = k.pos.x - Math.cos(k.heading) * L * 0.42;
      const rearY = k.pos.y - Math.sin(k.heading) * L * 0.42;
      const speed = Math.hypot(k.vel.x, k.vel.y);

      if (k.driftActive && speed > 90) {
        const prev = this.lastRear.get(k.id);
        if (prev && Math.hypot(prev.x - rearX, prev.y - rearY) < 60) {
          this.skids.push({ x1: prev.x, y1: prev.y, x2: rearX, y2: rearY, life: 2.6, max: 2.6 });
        }
        if (Math.random() < 0.55) {
          this.particles.push({
            x: rearX,
            y: rearY,
            vx: -k.vel.x * 0.15 + (Math.random() - 0.5) * 60,
            vy: -k.vel.y * 0.15 + (Math.random() - 0.5) * 60,
            life: 0.32,
            max: 0.32,
            size: 2 + Math.random() * 2,
            color: k.driftLevel > 1 ? '#7fe4ff' : '#ffc857',
          });
        }
      }

      if (k.surface === 'boost' && speed > 60) {
        this.particles.push({
          x: rearX,
          y: rearY,
          vx: -k.vel.x * 0.25 + (Math.random() - 0.5) * 30,
          vy: -k.vel.y * 0.25 + (Math.random() - 0.5) * 30,
          life: 0.26,
          max: 0.26,
          size: 2.5 + Math.random() * 2.5,
          color: Math.random() < 0.5 ? '#ffc857' : '#ff8a3d',
        });
      }

      if (k.wallHit > 0.16 && speed > 40) {
        this.burst(k.pos.x, k.pos.y, '#ffd166', 4, 90);
      }

      if (k.boostTimer > 0) {
        const bx = k.pos.x - Math.cos(k.heading) * L * 0.55;
        const by = k.pos.y - Math.sin(k.heading) * L * 0.55;
        for (let i = 0; i < 2; i++) {
          this.particles.push({
            x: bx + (Math.random() - 0.5) * 8,
            y: by + (Math.random() - 0.5) * 8,
            vx: -k.vel.x * 0.35 + (Math.random() - 0.5) * 40,
            vy: -k.vel.y * 0.35 + (Math.random() - 0.5) * 40,
            life: 0.28,
            max: 0.28,
            size: 3 + Math.random() * 3,
            color: Math.random() < 0.5 ? '#ffd166' : '#ff8a3d',
          });
        }
      }

      if (k.surface === 'sidewalk' && speed > 70 && Math.random() < 0.3) {
        this.particles.push({
          x: rearX,
          y: rearY,
          vx: (Math.random() - 0.5) * 50,
          vy: (Math.random() - 0.5) * 50,
          life: 0.5,
          max: 0.5,
          size: 3 + Math.random() * 4,
          color: 'rgba(214,204,184,0.85)',
        });
      }

      this.lastRear.set(k.id, { x: rearX, y: rearY });
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.94;
      p.vy *= 0.94;
    }
    for (let i = this.skids.length - 1; i >= 0; i--) {
      const s = this.skids[i];
      s.life -= dt;
      if (s.life <= 0) this.skids.splice(i, 1);
    }
    if (this.particles.length > 700) this.particles.splice(0, this.particles.length - 700);
    if (this.skids.length > 900) this.skids.splice(0, this.skids.length - 900);
  }

  burst(x: number, y: number, color: string, count = 14, speed = 150): void {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const v = speed * (0.4 + Math.random() * 0.8);
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        life: 0.5 + Math.random() * 0.25,
        max: 0.75,
        size: 2.5 + Math.random() * 3.5,
        color,
      });
    }
  }

  clear(): void {
    this.particles = [];
    this.skids = [];
    this.lastRear.clear();
  }

  drawSkids(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.lineCap = 'round';
    for (const s of this.skids) {
      const a = 0.16 * (s.life / s.max);
      ctx.strokeStyle = `rgba(16,18,24,${a.toFixed(3)})`;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(s.x1, s.y1);
      ctx.lineTo(s.x2, s.y2);
      ctx.stroke();
    }
    ctx.restore();
  }

  drawParticles(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    for (const p of this.particles) {
      const a = Math.max(0, p.life / p.max);
      ctx.globalAlpha = a;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * (0.6 + a * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
